import { FC, useState } from 'react'
import 'react-date-range/dist/styles.css' // main style file
import 'react-date-range/dist/theme/default.css' // theme css file
import { Link, useNavigate } from 'react-router-dom'
import MeetingRoomList from './MeetingroomList'

type MeetingRoom = {
  id: string
  name: string
  location: string
  capacity: number
  available: boolean
}

type Reservation = {
  id: string
  roomId: string
  date: string
  startTime: string
  endTime: string
  title: string
  organizer: string
  participants: string[]
}

// 샘플 데이터
const SAMPLE_ROOMS: MeetingRoom[] = [
  {
    id: '1',
    name: '대회의실',
    location: '3층',
    capacity: 20,
    available: true,
  },
  {
    id: '2',
    name: '소회의실 A',
    location: '2층',
    capacity: 8,
    available: true,
  },
  {
    id: '3',
    name: '소회의실 B',
    location: '2층',
    capacity: 8,
    available: false,
  },
  {
    id: '4',
    name: '임원 회의실',
    location: '4층',
    capacity: 12,
    available: true,
  },
]

const SAMPLE_RESERVATIONS: Reservation[] = [
  {
    id: '1',
    roomId: '1',
    date: '2023-06-15',
    startTime: '10:00',
    endTime: '11:30',
    title: '프로젝트 킥오프 미팅',
    organizer: '김철수',
    participants: ['김철수', '이영희', '박지성', '최민수'],
  },
  {
    id: '2',
    roomId: '2',
    date: '2023-06-15',
    startTime: '13:00',
    endTime: '14:00',
    title: '주간 팀 미팅',
    organizer: '이영희',
    participants: ['이영희', '박지성'],
  },
  {
    id: '3',
    roomId: '4',
    date: '2023-06-16',
    startTime: '15:00',
    endTime: '16:00',
    title: '클라이언트 미팅',
    organizer: '박지성',
    participants: ['박지성', '최민수', '김철수'],
  },
]

interface DashboardPageProps {
  onLogout: () => void
}

const Dashboard: FC<DashboardPageProps> = ({ onLogout }) => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('overview')
  const [rooms] = useState<MeetingRoom[]>(SAMPLE_ROOMS)
  const [reservations] = useState<Reservation[]>(SAMPLE_RESERVATIONS)
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())

  const handleLogout = () => {
    onLogout()
    navigate('/')
  }

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-50 w-full border-b bg-white">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-2 font-bold">
            <span className="text-blue-600">회의실 예약 시스템</span>
          </div>
          <nav className="flex items-center gap-4">
            <span className="text-sm text-gray-500">안녕하세요, 사용자님</span>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              로그아웃
            </button>
          </nav>
        </div>
      </header>
      <div className="container flex-1 py-8">
        <div className="mb-8 flex items-center justify-between">
          <h1 className="text-3xl font-bold">대시보드</h1>
          <Link
            to="/reservation"
            className="inline-flex h-10 items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mr-2 h-4 w-4"
            >
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
            새 예약
          </Link>
        </div>

        <div className="mb-8">
          <div className="flex space-x-4 border-b">
            <button
              className={`pb-2 pt-2 text-sm font-medium ${
                activeTab === 'overview'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('overview')}
            >
              개요
            </button>
            <button
              className={`pb-2 pt-2 text-sm font-medium ${
                activeTab === 'rooms'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('rooms')}
            >
              회의실
            </button>
            <button
              className={`pb-2 pt-2 text-sm font-medium ${
                activeTab === 'reservations'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('reservations')}
            >
              예약 현황
            </button>
          </div>
        </div>

        {activeTab === 'overview' && <MeetingRoomList />}

        {activeTab === 'rooms' && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {rooms.map((room) => (
              <div
                key={room.id}
                className="rounded-lg border bg-white p-6 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">{room.name}</h3>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      room.available
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {room.available ? '이용 가능' : '사용 중'}
                  </span>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center text-sm text-gray-500">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-2 h-4 w-4"
                    >
                      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    위치: {room.location}
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-2 h-4 w-4"
                    >
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    수용 인원: {room.capacity}명
                  </div>
                </div>
                <div className="mt-6">
                  <button
                    className="inline-flex h-9 w-full items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
                    disabled={!room.available}
                  >
                    예약하기
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'reservations' && (
          <div className="rounded-lg border bg-white shadow-sm">
            <div className="p-6">
              <h3 className="text-lg font-medium">예약 현황</h3>
              <div className="mt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        const prevDate = new Date(selectedDate)
                        prevDate.setDate(prevDate.getDate() - 1)
                        setSelectedDate(prevDate)
                      }}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-sm"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-4 w-4"
                      >
                        <path d="m15 18-6-6 6-6" />
                      </svg>
                      <span className="sr-only">이전 날짜</span>
                    </button>
                    <span className="text-sm font-medium">
                      {selectedDate.toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        weekday: 'long',
                      })}
                    </span>
                    <button
                      onClick={() => {
                        const nextDate = new Date(selectedDate)
                        nextDate.setDate(nextDate.getDate() + 1)
                        setSelectedDate(nextDate)
                      }}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-sm"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-4 w-4"
                      >
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                      <span className="sr-only">다음 날짜</span>
                    </button>
                  </div>
                  <button
                    onClick={() => setSelectedDate(new Date())}
                    className="inline-flex h-8 items-center justify-center rounded-md border px-3 text-sm"
                  >
                    오늘
                  </button>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50 text-left text-sm font-medium text-gray-500">
                    <th className="px-6 py-3">회의실</th>
                    <th className="px-6 py-3">시간</th>
                    <th className="px-6 py-3">제목</th>
                    <th className="px-6 py-3">주최자</th>
                    <th className="px-6 py-3">참석자</th>
                    <th className="px-6 py-3">액션</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {reservations
                    .filter(
                      (res) =>
                        new Date(res.date).toDateString() ===
                        selectedDate.toDateString()
                    )
                    .map((reservation) => {
                      const room = rooms.find(
                        (r) => r.id === reservation.roomId
                      )
                      return (
                        <tr key={reservation.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium">
                            {room?.name}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {reservation.startTime} - {reservation.endTime}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium">
                            {reservation.title}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {reservation.organizer}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {reservation.participants.length}명
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <button className="text-blue-600 hover:text-blue-800">
                              상세보기
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  {reservations.filter(
                    (res) =>
                      new Date(res.date).toDateString() ===
                      selectedDate.toDateString()
                  ).length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-4 text-center text-sm text-gray-500"
                      >
                        해당 날짜에 예약된 회의가 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Dashboard
